import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Divider as _Divider, Paper, Typography } from '@material-ui/core';
import { parse } from 'qs';
import { ClassAttributes, createElement, ReactElement, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { CreationSet } from '../../../service/persistent/CreationSetWebApi';
import CreativeBriefDetail from '../../component/compound/CreativeBriefDetail';
import DummyCreativeBriefDetail from '../../component/compound/DummyCreativeBriefDetail';
import DummyProjectTypeSummary from '../../component/compound/DummyCreativeBriefDetail/DummyProjectTypeSummary';
import ProjectBasicSettingDetail from '../../component/compound/ProjectBasicSettingDetail';
import ProjectDesignInspiration from '../../component/compound/ProjectDesignInspiration';
import ProjectIssue from '../../component/compound/ProjectIssue';
import ProjectTypeSummary from '../../component/compound/ProjectTypeSummary';
import { SPACING_LARGE, SPACING_UNIT } from '../../constant/spacing';
import { context as StateContext } from '../../context/state';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import useCreateProjectIssue from './useCreateProjectIssue';
import useCreateProjectIssueComment from './useCreateProjectIssueComment';
import useDeleteProjectIssueComment from './useDeleteProjectIssueComment';
import useUpdateProjectIssueComment from './useUpdateProjectIssueComment';

type Props = ClassAttributes<HTMLElement> & {
  creationSet: CreationSet;
  hasProjectChange: boolean;
  assetFiles: ReadonlyArray<FileReference>;
  briefFiles: Map<BucketId, FileReference[]>;
  loading: boolean;
  className?: string;
  reload: () => void;
};

const CreativeBrief = ({
  creationSet,
  hasProjectChange,
  assetFiles,
  briefFiles,
  loading,
  reload,
  className,
}: Props) => {
  const { createProjectIssue } = useCreateProjectIssue();
  const { createProjectIssueComment } = useCreateProjectIssueComment();
  const { updateProjectIssueComment } = useUpdateProjectIssueComment();
  const { deleteProjectIssueComment } = useDeleteProjectIssueComment();

  let projectTypeSummary: ReactElement<any>;
  let projectDesignInspiration: ReactElement<any> | null = null;
  let brief: ReactElement<any>;
  let basicSetting: ReactElement<any> | null = null;
  let projectIssue: ReactElement<any> | null = null;

  const project = creationSet.project;
  // オプション
  const _targetCreativeFormat = creationSet.creativeFormats[0];
  const selectedPricingMerchandiseOptions = _targetCreativeFormat
    ? _targetCreativeFormat.selectedPricingMerchandiseOptions || []
    : [];

  const state = useContext(StateContext);
  const { comment: selectedProjectIssueId } = parse(state.location.search.slice(1));

  useEffect(() => {
    // In-Page Link
    const targetElement = document.getElementById(selectedProjectIssueId);

    if (targetElement) {
      const rectTop = targetElement.getBoundingClientRect().top;
      const offsetTop = window.pageYOffset;
      const option = -1100;
      const headBarHeight = 60;
      const top = rectTop + offsetTop + option - headBarHeight;
      console.log(top);
      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    }
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.project.fetching === 0]);

  if (loading || !project) {
    projectTypeSummary = <DummyProjectTypeSummary />;
    brief = <DummyCreativeBriefDetail />;
  } else {
    projectTypeSummary = (
      <ProjectTypeSummary
        projectId={project.id}
        projectType={project.projectType}
        hasProjectChange={hasProjectChange}
      />
    );
    if (project.designInspirationCase) {
      projectDesignInspiration = <ProjectDesignInspiration designInspirationCase={project.designInspirationCase} />;
    }

    brief = (
      <CreativeBriefDetail
        definition={project.creativeBriefDefinition}
        value={project.creativeBriefValue}
        files={briefFiles}
      />
    );
    if (project != null) {
      basicSetting = (
        <ProjectBasicSettingDetail
          assetCategoryValue={project.assetCategoryValue}
          assetCategorySections={
            project.assetCategoryDefinition != null ? project.assetCategoryDefinition.sections : null
          }
          assetFiles={assetFiles}
          selectedPricingMerchandiseOptions={selectedPricingMerchandiseOptions}
        />
      );
      projectIssue = (
        <ProjectIssue
          project={project}
          onSubmitProjectIssue={(projectId: string, inputComment: string) =>
            createProjectIssue(projectId, inputComment, reload)
          }
          onSubmitProjectIssueComment={(issueId: string, comment: string) =>
            createProjectIssueComment(issueId, comment, reload)
          }
          onUpdateProjectIssueComment={(issueId: string, comment: string) =>
            updateProjectIssueComment(issueId, comment, reload)
          }
          onDeleteProjectIssueComment={(commentId: string) => deleteProjectIssueComment(commentId, reload)}
        />
      );
    }
  }

  return (
    <Root className={className}>
      <Section>
        <Heading variant="h5">
          <FormattedMessage id="project_detail_page.creative_brief.heading" />
        </Heading>
        <Content>
          {projectTypeSummary}
          <Divider />
          {projectDesignInspiration}
          {projectDesignInspiration ? <Divider /> : null}
          {basicSetting}
          {basicSetting ? <Divider /> : null}
          {brief}
        </Content>
      </Section>
      <Section>{projectIssue}</Section>
    </Root>
  );
};

const Root = styled('div')({
  ...gridLayoutContainer({
    columns: ['100%'],
    rows: ['auto', 'auto'],
    areas: [['heading'], ['content']],
  }),
});

const Heading = styled(Typography)({ ...gridLayoutItem('heading'), marginBottom: SPACING_UNIT * 3 });

const Content = styled(Paper)({
  ...gridLayoutItem('content'),
  padding: `${SPACING_UNIT * 3}px ${SPACING_UNIT * 2}px`,
});

const Divider = styled(_Divider)({
  margin: `${SPACING_UNIT * 3}px 0`,
});

const Section = styled('div')({
  marginBottom: SPACING_LARGE,
});

export default CreativeBrief;
